import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Clock, User, Calendar } from "lucide-react";
import ConsultationModal from "@/components/ConsultationModal";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BlogDetailPage() {
  const { slug } = useParams();
  const [blog, setBlog] = useState(null);

  useEffect(() => {
    axios.get(`${API}/blogs/${slug}`).then(r => setBlog(r.data)).catch(console.error);
  }, [slug]);

  if (!blog) return <div className="flex items-center justify-center h-96"><div className="loading-bar" /></div>;

  const paragraphs = blog.content.split('\n\n').filter(Boolean);

  return (
    <div data-testid="blog-detail-page">
      <section className="bg-gradient-to-b from-orange-50 to-white py-12 md:py-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <Link to="/blog" className="inline-flex items-center gap-1 text-orange-600 text-sm font-medium mb-6 hover:underline">
            <ArrowLeft className="h-4 w-4" /> Back to Blog
          </Link>
          <Badge className="bg-orange-100 text-orange-700 border-orange-200 mb-4">{blog.category}</Badge>
          <h1 className="font-heading text-3xl sm:text-4xl font-semibold text-slate-900 mb-4 leading-tight" data-testid="blog-detail-title">{blog.title}</h1>
          <div className="flex items-center gap-4 text-sm text-slate-500">
            <span className="flex items-center gap-1"><User className="h-3.5 w-3.5" /> {blog.author}</span>
            <span className="flex items-center gap-1"><Calendar className="h-3.5 w-3.5" /> {blog.date}</span>
            <span className="flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> {blog.read_time}</span>
          </div>
        </div>
      </section>

      <section className="py-10">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <div className="blog-content" data-testid="blog-content">
            {paragraphs.map((p, i) => {
              if (p.startsWith('**') && p.endsWith('**')) {
                return <h2 key={i}>{p.replace(/\*\*/g, '')}</h2>;
              }
              if (p.startsWith('**')) {
                const parts = p.split('**');
                return (
                  <p key={i}>
                    {parts.map((part, j) => j % 2 === 1 ? <strong key={j}>{part}</strong> : part)}
                  </p>
                );
              }
              if (p.startsWith('- ')) {
                const items = p.split('\n').filter(Boolean);
                return (
                  <ul key={i} className="list-disc">
                    {items.map((item, j) => <li key={j}>{item.replace(/^- /, '').replace(/\*\*/g, '')}</li>)}
                  </ul>
                );
              }
              return <p key={i}>{p}</p>;
            })}
          </div>

          <div className="bg-orange-50 rounded-2xl p-8 mt-12 text-center">
            <h3 className="font-heading text-2xl font-semibold text-slate-900 mb-3">Need Expert Medical Advice?</h3>
            <p className="text-slate-500 text-sm mb-6">Get a free consultation from our medical team within 24 hours.</p>
            <ConsultationModal
              trigger={
                <Button className="bg-orange-600 hover:bg-orange-700 text-white rounded-full px-8 font-semibold" data-testid="blog-cta-btn">
                  Get Free Consultation
                </Button>
              }
            />
          </div>
        </div>
      </section>
    </div>
  );
}
